import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterListIcon from "@mui/icons-material/FilterList";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FONT = "'Inter', 'Roboto', sans-serif";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const STATUS_OPTIONS = ["All", "Present", "Half Day", "Absent"];

function generateFallback() {
  const today = new Date();
  const rows = [];
  for (let i = 9; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const dow = d.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const dateStr = d.toISOString().slice(0, 10);
    if (isWeekend) continue;
    const present = Math.random() > 0.2;
    const checkin = present
      ? new Date(d.setHours(8, Math.floor(Math.random() * 30), 0)).toISOString()
      : null;
    const checkout =
      present && Math.random() > 0.3
        ? new Date(d.setHours(17, Math.floor(Math.random() * 30), 0)).toISOString()
        : null;

    let status = "Absent";
    if (checkin && checkout) {
      const diff =
        (new Date(checkout) - new Date(checkin)) / (1000 * 60 * 60);
      status = diff >= 6 ? "Present" : "Half Day";
    } else if (checkin) {
      status = "Half Day";
    }

    let duration = "—";
    if (checkin && checkout) {
      const mins = Math.round(
        (new Date(checkout) - new Date(checkin)) / 60000
      );
      duration = `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }

    rows.push({ date: dateStr, checkin, checkout, status, duration });
  }
  return rows;
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
}

function statusChipProps(status) {
  if (status === "Present") return { bg: "#e8f5e9", color: "#4caf50" };
  if (status === "Half Day") return { bg: "#fff3e0", color: "#ff9800" };
  if (status === "Absent") return { bg: "#ffebee", color: "#f44336" };
  return { bg: "#f3f4f6", color: "#8F95B2" };
}

export default function CheckInOut() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year] = useState(today.getFullYear());
  const [statusFilter, setStatusFilter] = useState("All");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(`staff/checkin/history?month=${month}&year=${year}`);
      const data = res?.data;
      if (Array.isArray(data) && data.length > 0) {
        setRows(data);
      } else {
        setRows(generateFallback());
      }
    } catch {
      setRows(generateFallback());
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleExport = async () => {
    try {
      const res = await fetchData("staff/checkin/export");
      if (res?.data?.url) {
        window.open(res.data.url, "_blank");
        return;
      }
    } catch {
      // fallback: CSV export
    }
    // Client-side CSV fallback
    const headers = ["Date", "Check-In Time", "Check-Out Time", "Status", "Duration"];
    const csvRows = [
      headers.join(","),
      ...filteredRows.map((r) =>
        [
          r.date,
          formatTime(r.checkin),
          formatTime(r.checkout),
          r.status,
          r.duration,
        ].join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff_checkin_${month}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ open: true, severity: "success", message: "Exported successfully" });
  };

  const filteredRows =
    statusFilter === "All" ? rows : rows.filter((r) => r.status === statusFilter);

  const presentCount = rows.filter((r) => r.status === "Present").length;
  const halfDayCount = rows.filter((r) => r.status === "Half Day").length;
  const absentCount = rows.filter((r) => r.status === "Absent").length;

  return (
    <Box
      sx={{
        padding: "1.5rem",
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily: FONT,
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 0.5 }}>
        Pages / Check-In / Check-Out
      </Typography>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e", fontFamily: FONT }}>
            Attendance History
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
            <Chip label={`${presentCount} Present`} size="small" sx={{ bg: "#e8f5e9", backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 700, fontSize: "11px" }} />
            <Chip label={`${halfDayCount} Half Day`} size="small" sx={{ backgroundColor: "#fff3e0", color: "#ff9800", fontWeight: 700, fontSize: "11px" }} />
            <Chip label={`${absentCount} Absent`} size="small" sx={{ backgroundColor: "#ffebee", color: "#f44336", fontWeight: 700, fontSize: "11px" }} />
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          sx={{
            borderColor: "#244C77",
            color: "#244C77",
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#f0edff" },
          }}
        >
          Export
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "14px",
          mb: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <FilterListIcon sx={{ color: "#244C77", fontSize: "18px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1a1a2e" }}>Filters</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={(e) => setMonth(e.target.value)}
              sx={{ borderRadius: "10px", fontSize: "13px" }}
              startAdornment={
                <CalendarMonthIcon sx={{ color: "#8F95B2", fontSize: "16px", mr: 0.5 }} />
              }
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: "10px", fontSize: "13px" }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            px: 3,
            py: 2,
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px", fontFamily: FONT }}>
            Check-In / Check-Out Records
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>
            {MONTHS.find((m) => m.value === month)?.label} {year} — {filteredRows.length} records
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#244C77" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                  {["Date", "Check-In Time", "Check-Out Time", "Status", "Duration"].map(
                    (h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          color: "#374151",
                          fontFamily: FONT,
                          borderBottom: "2px solid #E5E7EB",
                          py: 1.5,
                        }}
                      >
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 5, color: "#8F95B2" }}>
                      No records found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, i) => {
                    const sp = statusChipProps(row.status);
                    return (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even)": { backgroundColor: "#FAFAFA" },
                          "&:hover": { backgroundColor: "#F3F0FF" },
                          transition: "background-color 0.15s",
                        }}
                      >
                        <TableCell
                          sx={{ fontSize: "13px", color: "#1a1a2e", fontFamily: FONT, py: 1.5 }}
                        >
                          {formatDate(row.date)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "13px",
                            color: row.checkin ? "#4caf50" : "#8F95B2",
                            fontWeight: row.checkin ? 600 : 400,
                            fontFamily: FONT,
                          }}
                        >
                          {formatTime(row.checkin)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "13px",
                            color: row.checkout ? "#f44336" : "#8F95B2",
                            fontWeight: row.checkout ? 600 : 400,
                            fontFamily: FONT,
                          }}
                        >
                          {formatTime(row.checkout)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            sx={{
                              backgroundColor: sp.bg,
                              color: sp.color,
                              fontWeight: 700,
                              fontSize: "11px",
                              borderRadius: "20px",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: "13px", color: "#374151", fontFamily: FONT }}
                        >
                          {row.duration}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
